import { _ } from "libs";
/// <reference types="googlemaps" />
import React from "react";
import { Form, Field, FormSpy } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import CourtsDateTimePicker from "./courtsDateTimePicker";
import { History } from "history";
import ErrorBoundary from "../errorBoundary";
import withRedux from "../../withRedux";
import { Profile, LocationSearchResult } from "../../types";
import { getTimezoneByLocation } from "../../actions";
import AsyncButton from "../asyncButton";
import { getLocalMomentInZone } from "../../libs/time";
import CityAndClubSelect from "../forms/controls/cityAndClubSelect";
import Autosave from "../forms/autosave";

const DateTimeSelect = ({ input: { onChange, value, name }, timezone, ...rest }) => {
	return <CourtsDateTimePicker onChange={onChange} value={value} timezone={timezone} />;
};

type Values = {
	location: LocationSearchResult;
	timestamp: number;
	timezone: string;
};

const WhenFieldChanges = ({ field, set }: { field: string; set: string }) => (
	<Field name={set} subscription={{}}>
		{(
			// No subscription. We only use Field to get to the change function
			{ input: { onChange } }
		) => (
			<OnChange name={field}>
				{async (city: LocationSearchResult) => {
					if (city.location) {
						const timezone = await getTimezoneByLocation(city.location.lat, city.location.lng);
						onChange((timezone as unknown) as React.ChangeEvent<string>);
					}
				}}
			</OnChange>
		)}
	</Field>
);

const CourtForm = ({
	history,
	showSubmitButton = true,
	timestamp = null,
	profile,
	city,
	onAutoSave,
	timezone,
}: {
	history: History;
	showSubmitButton: boolean;
	timestamp?: number;
	profile: Profile;
	city?: string;
	onAutoSave?: (values: object) => void;
	timezone: string;
}) => {
	if (!timestamp) {
		timestamp = getLocalMomentInZone(timezone)
			.add(1, "h")
			.unix();
	}

	const initialValues = {
		timestamp,
		location: {
			value: city || profile.city,
			type: "place",
		},
		timezone,
	} as Values;

	const geocoder = new google.maps.Geocoder();
	const onSubmit = (values: Values) => {
		const location = values.location;
		if (values.location.type === "club") {
			history.push(`/club/${values.location.club.clubId}`, {
				timestamp: values.timestamp,
			});
		}

		if (values.location.type === "place") {
			if (location.location) {
				history.push("/courts/results", {
					latitude: location.location.lat,
					longitude: location.location.lng,
					timestamp: values.timestamp,
					city: values.location.value,
					location: values.location,
					timezone: values.timezone,
				});
			} else {
				geocoder.geocode(
					{ address: values.location.value },
					(results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
						if (results && results[0]) {
							const center = results[0].geometry.location;
							history.push("/courts/results", {
								latitude: center.lat(),
								longitude: center.lng(),
								timestamp: values.timestamp,
								city: location.value,
								location,
								timezone,
							});
						}
					}
				);
			}
		}
	};

	return (
		<ErrorBoundary>
			<Form
				onSubmit={onSubmit}
				initialValues={initialValues}
				render={({ handleSubmit, form, submitting, pristine, values }) => (
					<form onSubmit={handleSubmit} className="searchCourts">
						<WhenFieldChanges field="location" set="timezone" />

						<div className="fields">
							<Field name="location">
								{({ input, meta, ...rest }) => (
									<div className="field small searchField location">
										<label>{_("courtForm.location.label")}</label>
										<CityAndClubSelect
											{...{ input, meta, ...rest }}
											getDisplayValue={value => value.value}
										/>
										{meta.touched && meta.error && <span>{meta.error}</span>}
									</div>
								)}
							</Field>
							<Field name="timestamp">
								{({ input, meta, ...rest }) => (
									<DateTimeSelect {...{ input, meta, ...rest }} timezone={values.timezone} />
								)}
							</Field>
							<Field name="timezone">
								{({ input, meta, ...rest }) => <input {...input} type={"hidden"} />}
							</Field>
						</div>
						<div className="action">
							{showSubmitButton ? (
								<AsyncButton type="submit" className="button">
									{_("courtForm.submit.label")}
								</AsyncButton>
							) : (
								<Autosave onAutoSave={onAutoSave} />
							)}
						</div>
					</form>
				)}
			/>
		</ErrorBoundary>
	);
};

function mapStateToProps({ profile }) {
	return { profile };
}

export default withRedux({}, mapStateToProps)(CourtForm);
